import { useState, useEffect } from 'react';
import { Wrench, CheckCircle, Clock, X, Smartphone, History, Printer, Ban, ArrowRightLeft, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { busesApi, pagosMantenimientoApi, precobranzaMantenimientoApi } from '../lib/api';
import TicketPrecobranzaMantenimiento from '../components/TicketPrecobranzaMantenimiento';
import AnularModal from '../components/AnularModal';
import { filtrarBuses } from '../components/BusSelect';

const METODOS = [
  { valor: 'efectivo', label: 'Efectivo' },
  { valor: 'yape_plin', label: 'Yape / Plin' },
  { valor: 'transferencia', label: 'Transferencia / Depósito' },
  { valor: 'pos', label: 'POS' },
];

// Etiqueta legible del periodo "YYYY-MM-01/xx" / "YYYY-MM-xx/fin" -> "1ra Quincena - Agosto 2026"
// No depende del dia exacto de corte (14, 15 o 16): solo mira si el rango
// empieza en "01" (primera quincena) o no (segunda).
const MESES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
function etiquetaPeriodo(periodo: string) {
  const [anio, mes, rango] = periodo.split('-');
  const nombreMes = MESES[parseInt(mes, 10) - 1] || mes;
  const esPrimeraQuincena = rango.startsWith('01');
  return `${esPrimeraQuincena ? '1ra' : '2da'} Quincena - ${nombreMes} ${anio}`;
}

// Mismo corte que periodoActual() en el backend (simple-api.js): 14/31 desde
// setiembre 2026, 15/16 antes (la quincena de agosto ya tiene pagos reales bajo
// esa etiqueta vieja, no se puede cambiar a mitad de camino).
function periodoDeFecha(fecha: Date) {
  const anio = fecha.getFullYear();
  const mes = fecha.getMonth() + 1;
  const mesStr = String(mes).padStart(2, '0');
  const usaCorteNuevo = anio > 2026 || (anio === 2026 && mes >= 9);
  if (usaCorteNuevo) {
    return fecha.getDate() <= 14 ? `${anio}-${mesStr}-01/14` : `${anio}-${mesStr}-15/fin`;
  }
  return fecha.getDate() <= 15 ? `${anio}-${mesStr}-01/15` : `${anio}-${mesStr}-16/fin`;
}

// Lista de periodos recientes (actual + anteriores) para el selector por bus,
// para poder cobrar/emitir el recibo de una quincena atrasada sin cambiar el
// periodo que muestra el resto de la pantalla.
function periodosRecientes(cantidad = 6) {
  const opciones: { value: string; label: string }[] = [];
  const vistos = new Set<string>();
  // Retrocede de a 15 dias hasta juntar "cantidad" periodos distintos.
  const cursor = new Date();
  while (opciones.length < cantidad) {
    const p = periodoDeFecha(cursor);
    if (!vistos.has(p)) {
      vistos.add(p);
      opciones.push({ value: p, label: etiquetaPeriodo(p) });
    }
    cursor.setDate(cursor.getDate() - 15);
  }
  return opciones;
}

const PagoMantenimiento = () => {
  const [busquedaBus, setBusquedaBus] = useState('');
  const [periodo, setPeriodo] = useState('');
  const [monto, setMonto] = useState(280);
  const [buses, setBuses] = useState<any[]>([]);
  const [pendientesIds, setPendientesIds] = useState<Set<string>>(new Set());
  const [cargando, setCargando] = useState(true);
  const [busParaPagar, setBusParaPagar] = useState<any>(null);
  const [mostrarHistorial, setMostrarHistorial] = useState(false);
  const [historial, setHistorial] = useState<any[]>([]);
  const [precobranzaParaImprimir, setPrecobranzaParaImprimir] = useState<any>(null);
  const [imprimiendoPrecobranza, setImprimiendoPrecobranza] = useState<string | null>(null);
  const [periodoPorBus, setPeriodoPorBus] = useState<Record<string, string>>({});
  const [pagoParaAnular, setPagoParaAnular] = useState<any>(null);
  const [busParaPrecobranza, setBusParaPrecobranza] = useState<any>(null);
  const [pagoParaMover, setPagoParaMover] = useState<any>(null);
  const opcionesPeriodo = periodosRecientes();
  const periodoDeBus = (busId: string) => periodoPorBus[busId] || periodo;

  const cargarDatos = async () => {
    setCargando(true);
    try {
      const [pendientesRes, todosBuses] = await Promise.all([
        pagosMantenimientoApi.getPendientes(),
        busesApi.getAll()
      ]);
      setPeriodo(pendientesRes.periodo);
      setMonto(pendientesRes.monto);
      setPendientesIds(new Set(pendientesRes.pendientes.map((b: any) => b.id)));
      setBuses((todosBuses || []).filter((b: any) => b.estado === 'activo'));
    } catch (error: any) {
      toast.error('Error al cargar: ' + error.message);
    } finally {
      setCargando(false);
    }
  };

  const cargarHistorial = async () => {
    try {
      const data = await pagosMantenimientoApi.getAll();
      setHistorial(data || []);
    } catch (error: any) {
      toast.error('Error al cargar historial: ' + error.message);
    }
  };

  useEffect(() => { cargarDatos(); }, []);

  useEffect(() => {
    if (precobranzaParaImprimir) {
      const timer = setTimeout(() => {
        window.print();
        setPrecobranzaParaImprimir(null);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [precobranzaParaImprimir]);

  const imprimirPrecobranza = async (bus: any, conceptos: ConceptoPedido[]) => {
    setImprimiendoPrecobranza(bus.id);
    try {
      const { precobranza } = await precobranzaMantenimientoApi.crear(bus.padron, periodoDeBus(bus.id), conceptos);
      setBusParaPrecobranza(null);
      setPrecobranzaParaImprimir(precobranza);
    } catch (error: any) {
      toast.error('Error al generar el recibo: ' + error.message);
    } finally {
      setImprimiendoPrecobranza(null);
    }
  };

  // Buscador por teclado: padron (parcial), placa o dueno
  const busesVisibles = filtrarBuses(buses, busquedaBus);
  const buses_pendientes = busesVisibles.filter(b => pendientesIds.has(b.id));
  const buses_pagados = busesVisibles.filter(b => !pendientesIds.has(b.id));
  // Monto real de un bus: la mayoria usa el monto general, pero un bus puntual puede
  // tener su propia excepcion guardada en buses.monto_mantenimiento (ej. bus 86 = S/200).
  const montoDeBus = (bus: any) => bus.monto_mantenimiento != null ? parseFloat(bus.monto_mantenimiento) : monto;

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Wrench className="text-blue-600" />
            Pago de Mantenimiento
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Cuota fija por bus, independiente de los repuestos comprados en el taller. Se cobra dos veces al mes (1ra y 2da quincena).
          </p>
        </div>
        <button
          onClick={() => { setMostrarHistorial(v => !v); if (!mostrarHistorial) cargarHistorial(); }}
          className="flex items-center gap-2 px-4 py-2 border rounded-lg hover:bg-gray-50 text-sm"
        >
          <History size={16} />
          {mostrarHistorial ? 'Ver periodo actual' : 'Ver historial completo'}
        </button>
      </div>

      {!mostrarHistorial && (
        <>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex justify-between items-center">
            <div>
              <p className="text-sm text-blue-700">Periodo actual</p>
              <p className="text-lg font-bold text-blue-900">{periodo ? etiquetaPeriodo(periodo) : '-'}</p>
            </div>
            <div className="text-right">
              <p className="text-sm text-blue-700">Monto fijo por bus</p>
              <p className="text-lg font-bold text-blue-900">S/ {monto.toFixed(2)}</p>
            </div>
          </div>

          {cargando ? (
            <p className="text-center text-gray-500 py-8">Cargando...</p>
          ) : (
            <>
              <input
                type="text"
                inputMode="search"
                autoComplete="off"
                value={busquedaBus}
                onChange={(e) => setBusquedaBus(e.target.value)}
                placeholder="Buscar por padrón, placa o dueño… (ej. 91)"
                className="w-full mb-4 p-2.5 border rounded-lg"
              />
              {busquedaBus && buses_pendientes.length === 0 && buses_pagados.length === 0 && (
                <p className="text-sm text-gray-500 mb-4">Ningún bus coincide con “{busquedaBus}”.</p>
              )}
              {buses_pendientes.length > 0 && (
                <div>
                  <h2 className="font-semibold text-red-700 mb-2 flex items-center gap-2">
                    <Clock size={18} /> Pendientes ({buses_pendientes.length})
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {buses_pendientes.map(bus => (
                      <div key={bus.id} className="bg-white rounded-lg shadow border-l-4 border-red-500 p-4 flex justify-between items-center gap-2">
                        <div>
                          <p className="font-bold">Padrón {bus.padron}</p>
                          <p className="text-sm text-gray-500">{bus.placa} - {bus.marca} {bus.modelo}</p>
                          {bus.dueno && <p className="text-sm text-gray-700">Dueño: <span className="font-semibold">{bus.dueno}</span></p>}
                          {bus.monto_mantenimiento != null && (
                            <p className="text-xs text-amber-600 font-semibold mt-0.5">Monto propio: S/ {montoDeBus(bus).toFixed(2)}</p>
                          )}
                        </div>
                        <div className="flex flex-col gap-1.5 items-end">
                          <select
                            value={periodoDeBus(bus.id)}
                            onChange={(e) => setPeriodoPorBus(prev => ({ ...prev, [bus.id]: e.target.value }))}
                            title="Quincena a cobrar/imprimir"
                            className="text-xs border rounded px-1.5 py-1 text-gray-600"
                          >
                            {opcionesPeriodo.map(op => <option key={op.value} value={op.value}>{op.label}</option>)}
                          </select>
                          <div className="flex gap-2">
                            <button
                              onClick={() => setBusParaPrecobranza(bus)}
                              disabled={imprimiendoPrecobranza === bus.id}
                              title="Imprimir recibo de precobranza"
                              className="px-2.5 py-1.5 border border-gray-300 text-gray-600 rounded-lg text-sm hover:bg-gray-50 disabled:opacity-50"
                            >
                              <Printer size={16} />
                            </button>
                            <button
                              onClick={() => setBusParaPagar(bus)}
                              className="px-3 py-1.5 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700"
                            >
                              Registrar pago
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <h2 className="font-semibold text-green-700 mb-2 flex items-center gap-2">
                  <CheckCircle size={18} /> Pagados este periodo ({buses_pagados.length})
                </h2>
                {buses_pagados.length === 0 ? (
                  <p className="text-gray-500 text-sm">Ningún bus pagó todavía este periodo.</p>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {buses_pagados.map(bus => (
                      <div key={bus.id} className="bg-white rounded-lg shadow border-l-4 border-green-500 p-4 flex justify-between items-center gap-2">
                        <div>
                          <p className="font-bold">Padrón {bus.padron}</p>
                          <p className="text-sm text-gray-500">{bus.placa} - {bus.marca} {bus.modelo}</p>
                          {bus.dueno && <p className="text-sm text-gray-700">Dueño: <span className="font-semibold">{bus.dueno}</span></p>}
                        </div>
                        <div className="flex flex-col gap-1.5 items-end">
                          <select
                            value={periodoDeBus(bus.id)}
                            onChange={(e) => setPeriodoPorBus(prev => ({ ...prev, [bus.id]: e.target.value }))}
                            title="Quincena a imprimir"
                            className="text-xs border rounded px-1.5 py-1 text-gray-600"
                          >
                            {opcionesPeriodo.map(op => <option key={op.value} value={op.value}>{op.label}</option>)}
                          </select>
                          <button
                            onClick={() => setBusParaPrecobranza(bus)}
                            disabled={imprimiendoPrecobranza === bus.id}
                            title="Imprimir recibo de precobranza"
                            className="px-2.5 py-1.5 border border-gray-300 text-gray-600 rounded-lg text-sm hover:bg-gray-50 disabled:opacity-50"
                          >
                            <Printer size={16} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </>
      )}

      {!mostrarHistorial && periodo && <PrecobranzasImpresas periodo={periodo} etiqueta={etiquetaPeriodo(periodo)} />}

      {mostrarHistorial && (
        <div className="bg-white rounded-lg shadow overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left">Bus</th>
                <th className="px-4 py-2 text-left">Periodo</th>
                <th className="px-4 py-2 text-left">Fecha de pago</th>
                <th className="px-4 py-2 text-right">Monto</th>
                <th className="px-4 py-2 text-left">Método</th>
                <th className="px-4 py-2 text-center">Acción</th>
              </tr>
            </thead>
            <tbody>
              {historial.length === 0 ? (
                <tr><td colSpan={6} className="text-center text-gray-500 py-6">Sin pagos registrados todavía</td></tr>
              ) : historial.map((p: any) => (
                <tr key={p.id} className="border-b">
                  <td className="px-4 py-2">Padrón {p.bus_padron}</td>
                  <td className="px-4 py-2">{etiquetaPeriodo(p.periodo)}</td>
                  <td className="px-4 py-2">{new Date(p.fecha_pago).toLocaleDateString('es-PE')}</td>
                  <td className="px-4 py-2 text-right font-semibold">S/ {parseFloat(p.monto).toFixed(2)}</td>
                  <td className="px-4 py-2">{METODOS.find(m => m.valor === p.metodo_pago)?.label || p.metodo_pago}</td>
                  <td className="px-4 py-2 text-center">
                    <div className="flex items-center justify-center gap-3">
                      <button
                        onClick={() => setPagoParaMover(p)}
                        title="Mover este pago a un servicio o al crédito del bus"
                        className="text-blue-600 hover:text-blue-800 text-xs underline flex items-center gap-1"
                      >
                        <ArrowRightLeft size={14} /> Mover
                      </button>
                      <button
                        onClick={() => setPagoParaAnular(p)}
                        title="Anular este pago"
                        className="text-red-600 hover:text-red-800 text-xs underline flex items-center gap-1"
                      >
                        <Ban size={14} /> Anular
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {busParaPagar && (
        <ModalRegistrarPago
          bus={busParaPagar}
          periodo={periodoDeBus(busParaPagar.id)}
          periodoActual={periodo}
          monto={montoDeBus(busParaPagar)}
          onClose={() => setBusParaPagar(null)}
          onPagado={() => { setBusParaPagar(null); cargarDatos(); }}
        />
      )}

      {precobranzaParaImprimir && (
        <TicketPrecobranzaMantenimiento
          precobranza={precobranzaParaImprimir}
          etiquetaPeriodo={etiquetaPeriodo}
        />
      )}

      {busParaPrecobranza && (
        <ModalPrecobranza
          bus={busParaPrecobranza}
          periodo={periodoDeBus(busParaPrecobranza.id)}
          montoMantenimiento={montoDeBus(busParaPrecobranza)}
          procesando={imprimiendoPrecobranza === busParaPrecobranza.id}
          onClose={() => setBusParaPrecobranza(null)}
          onImprimir={(conceptos) => imprimirPrecobranza(busParaPrecobranza, conceptos)}
        />
      )}

      {pagoParaMover && (
        <ModalMoverPago
          pago={pagoParaMover}
          bus={buses.find(b => b.id === pagoParaMover.bus_id)}
          onClose={() => setPagoParaMover(null)}
          onMovido={() => { setPagoParaMover(null); cargarHistorial(); cargarDatos(); }}
        />
      )}

      {pagoParaAnular && (
        <AnularModal
          titulo={`Anular pago — Padrón ${pagoParaAnular.bus_padron}`}
          descripcion={`Se anulará el pago de S/ ${parseFloat(pagoParaAnular.monto).toFixed(2)} (${etiquetaPeriodo(pagoParaAnular.periodo)}) y se revertirá el ingreso que generó en Caja Central. El bus volverá a aparecer como pendiente de ese periodo.`}
          textoBoton="Anular pago"
          onClose={() => setPagoParaAnular(null)}
          onConfirmar={async (clave, motivo) => {
            await pagosMantenimientoApi.anular(pagoParaAnular.id, { clave, motivo });
            toast.success('Pago anulado');
            setPagoParaAnular(null);
            cargarHistorial();
            cargarDatos();
          }}
        />
      )}
    </div>
  );
};

// Precobranzas (recibos de aviso) impresas en el periodo, con anulacion por clave de
// administrador + motivo (queda en Auditoria). Marca como "Duplicada" la que se imprimio
// otra vez para el mismo bus y periodo -- caso real: bus 1316, dos recibos el 14/10/2026.
const PrecobranzasImpresas = ({ periodo, etiqueta }: { periodo: string; etiqueta: string }) => {
  const [abierto, setAbierto] = useState(false);
  const [lista, setLista] = useState<any[]>([]);
  const [cargando, setCargando] = useState(false);
  const [paraAnular, setParaAnular] = useState<any>(null);

  const cargar = async () => {
    setCargando(true);
    try { setLista(await precobranzaMantenimientoApi.listar(periodo)); }
    catch (e: any) { toast.error('Error al cargar precobranzas: ' + e.message); }
    finally { setCargando(false); }
  };
  useEffect(() => { if (abierto) cargar(); }, [abierto, periodo]);

  // la mas antigua de cada bus es la original; las demas del mismo bus/periodo son duplicadas
  const vistas = new Set<string>();
  const duplicadas = new Set<string>();
  [...lista].sort((a, b) => new Date(a.fecha_impresion).getTime() - new Date(b.fecha_impresion).getTime())
    .forEach(p => { const k = p.bus_id + '|' + p.periodo; if (vistas.has(k)) duplicadas.add(p.id); else vistas.add(k); });

  return (
    <div className="bg-white rounded-lg shadow p-4">
      <button onClick={() => setAbierto(v => !v)} className="w-full flex justify-between items-center text-left">
        <span className="font-semibold flex items-center gap-2"><Printer size={18} className="text-sky-600" /> Precobranzas impresas — {etiqueta}</span>
        <span className="text-sm text-blue-600">{abierto ? 'Ocultar' : 'Ver / anular'}</span>
      </button>
      {abierto && (
        <div className="mt-3">
          {cargando ? <p className="text-sm text-gray-500">Cargando...</p> : lista.length === 0 ? (
            <p className="text-sm text-gray-500">No se imprimieron precobranzas en este periodo.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-gray-500 border-b">
                  <th className="py-1.5 pr-2">Padrón</th><th className="pr-2">Placa</th><th className="pr-2">Monto</th>
                  <th className="pr-2">Impresa</th><th className="pr-2">Por</th><th></th>
                </tr></thead>
                <tbody>
                  {lista.map(p => (
                    <tr key={p.id} className={`border-b last:border-0 ${duplicadas.has(p.id) ? 'bg-amber-50' : ''}`}>
                      <td className="py-1.5 pr-2 font-semibold">{p.bus_padron}
                        {duplicadas.has(p.id) && <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-amber-200 text-amber-800">Duplicada</span>}
                      </td>
                      <td className="pr-2">{p.bus_placa || '-'}</td>
                      <td className="pr-2">S/ {parseFloat(p.monto).toFixed(2)}</td>
                      <td className="pr-2 text-gray-600">{new Date(p.fecha_impresion).toLocaleString('es-PE', { timeZone: 'America/Lima', dateStyle: 'short', timeStyle: 'short' })}</td>
                      <td className="pr-2 text-gray-600">{p.registrado_por || '-'}</td>
                      <td className="text-right">
                        <button onClick={() => setParaAnular(p)} className="px-2 py-1 border border-red-300 text-red-600 rounded text-xs hover:bg-red-50 inline-flex items-center gap-1">
                          <Ban size={13} /> Anular
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
      {paraAnular && (
        <AnularModal
          titulo={`Anular precobranza — Padrón ${paraAnular.bus_padron}`}
          descripcion={`Se anulará el recibo de precobranza de S/ ${parseFloat(paraAnular.monto).toFixed(2)} impreso el ${new Date(paraAnular.fecha_impresion).toLocaleString('es-PE', { timeZone: 'America/Lima', dateStyle: 'short', timeStyle: 'short' })}. Es solo el aviso: no cambia pagos ni caja. Queda registrado en Auditoría con el motivo.`}
          textoBoton="Anular precobranza"
          onClose={() => setParaAnular(null)}
          onConfirmar={async (clave, motivo) => {
            await precobranzaMantenimientoApi.anular(paraAnular.id, { clave, motivo });
            toast.success('Precobranza anulada');
            setParaAnular(null);
            cargar();
          }}
        />
      )}
    </div>
  );
};

type ConceptoPedido = { tipo: string; monto?: number; detalle?: string };

// Conceptos que se pueden marcar en el recibo de precobranza. El mantenimiento usa el
// monto fijo del bus (lo pone el backend); los creditos se proponen con la deuda actual
// del bus y se pueden cambiar; el resto lleva monto y detalle a mano.
const OPCIONES_CONCEPTO = [
  { tipo: 'mantenimiento', label: 'Mantenimiento (cuota fija)' },
  { tipo: 'credito_productos', label: 'Crédito pendiente - repuestos' },
  { tipo: 'credito_servicios', label: 'Crédito pendiente - servicios' },
  { tipo: 'servicio', label: 'Servicio / reparación' },
  { tipo: 'repuestos', label: 'Repuestos' },
  { tipo: 'otro', label: 'Otro' },
];

const ModalPrecobranza = ({ bus, periodo, montoMantenimiento, procesando, onClose, onImprimir }: {
  bus: any; periodo: string; montoMantenimiento: number; procesando: boolean;
  onClose: () => void; onImprimir: (conceptos: ConceptoPedido[]) => void;
}) => {
  const deudaProductos = parseFloat(bus.credito_usado_productos) || 0;
  const deudaServicios = parseFloat(bus.credito_usado_servicios) || 0;
  const [marcados, setMarcados] = useState<Record<string, boolean>>({ mantenimiento: true });
  const [montos, setMontos] = useState<Record<string, string>>({
    credito_productos: deudaProductos > 0 ? deudaProductos.toFixed(2) : '',
    credito_servicios: deudaServicios > 0 ? deudaServicios.toFixed(2) : '',
  });
  const [detalles, setDetalles] = useState<Record<string, string>>({});
  const [error, setError] = useState('');

  const montoDe = (tipo: string) => tipo === 'mantenimiento' ? montoMantenimiento : (parseFloat(montos[tipo]) || 0);
  const total = OPCIONES_CONCEPTO.filter(o => marcados[o.tipo]).reduce((s, o) => s + montoDe(o.tipo), 0);

  const confirmar = () => {
    const elegidos = OPCIONES_CONCEPTO.filter(o => marcados[o.tipo]);
    if (elegidos.length === 0) { setError('Marca al menos un concepto'); return; }
    for (const o of elegidos) {
      if (o.tipo !== 'mantenimiento' && !(montoDe(o.tipo) > 0)) { setError(`Pon el monto de "${o.label}"`); return; }
      if (o.tipo === 'otro' && !(detalles.otro || '').trim()) { setError('Escribe qué se cobra en "Otro"'); return; }
    }
    setError('');
    onImprimir(elegidos.map(o => o.tipo === 'mantenimiento'
      ? { tipo: o.tipo }
      : { tipo: o.tipo, monto: montoDe(o.tipo), detalle: (detalles[o.tipo] || '').trim() || undefined }));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4">
      <div className="bg-white rounded-lg w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b flex justify-between items-center">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Printer className="text-blue-600" size={20} />
            Recibo de precobranza — Bus {bus.padron}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        <div className="p-6 space-y-3">
          <p className="text-sm text-gray-500">Marca qué se está cobrando. Es solo un aviso: no registra ningún pago.</p>
          {OPCIONES_CONCEPTO.map(o => {
            const activo = !!marcados[o.tipo];
            const deuda = o.tipo === 'credito_productos' ? deudaProductos : o.tipo === 'credito_servicios' ? deudaServicios : null;
            return (
              <div key={o.tipo} className={`border rounded-lg p-3 ${activo ? 'border-blue-400 bg-blue-50' : ''}`}>
                <label className="flex items-center justify-between gap-2 cursor-pointer">
                  <span className="flex items-center gap-2">
                    <input type="checkbox" checked={activo} onChange={(e) => setMarcados(prev => ({ ...prev, [o.tipo]: e.target.checked }))} />
                    <span className="font-medium text-sm">{o.label}</span>
                  </span>
                  {o.tipo === 'mantenimiento' && (
                    <span className="text-sm font-semibold">S/ {montoMantenimiento.toFixed(2)} · {etiquetaPeriodo(periodo)}</span>
                  )}
                  {deuda !== null && <span className="text-xs text-gray-500">Debe: S/ {deuda.toFixed(2)}</span>}
                </label>
                {activo && o.tipo !== 'mantenimiento' && (
                  <div className="flex gap-2 mt-2">
                    <input
                      type="text"
                      placeholder={o.tipo === 'otro' ? 'Qué se cobra (obligatorio)' : 'Detalle (opcional)'}
                      value={detalles[o.tipo] || ''}
                      maxLength={60}
                      onChange={(e) => setDetalles(prev => ({ ...prev, [o.tipo]: e.target.value }))}
                      className="flex-1 border rounded px-2 py-1 text-sm"
                    />
                    <input
                      type="number" min="0" step="0.01" placeholder="S/"
                      value={montos[o.tipo] || ''}
                      onChange={(e) => setMontos(prev => ({ ...prev, [o.tipo]: e.target.value }))}
                      className="w-28 border rounded px-2 py-1 text-sm text-right"
                    />
                  </div>
                )}
              </div>
            );
          })}
          <div className="flex justify-between items-center bg-gray-50 rounded-lg p-3">
            <span className="font-semibold">A cobrar</span>
            <span className="text-lg font-bold">S/ {total.toFixed(2)}</span>
          </div>
          {error && <p className="text-sm text-red-600">{error}</p>}
          <button
            onClick={confirmar}
            disabled={procesando}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-lg disabled:opacity-50 flex items-center justify-center gap-2"
          >
            <Printer size={18} /> {procesando ? 'Generando...' : 'Imprimir recibo'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Mover un pago de mantenimiento ya registrado: la plata sigue en caja (misma fecha y
// metodo), solo cambia a que se aplica. La quincena del bus vuelve a quedar pendiente.
const ModalMoverPago = ({ pago, bus, onClose, onMovido }: {
  pago: any; bus: any; onClose: () => void; onMovido: () => void;
}) => {
  const monto = parseFloat(pago.monto);
  const deudaProductos = parseFloat(bus?.credito_usado_productos) || 0;
  const deudaServicios = parseFloat(bus?.credito_usado_servicios) || 0;
  const [destino, setDestino] = useState<'credito' | 'servicio'>(deudaServicios > 0 || deudaProductos > 0 ? 'credito' : 'servicio');
  // Propuesta inicial: primero a servicios (el caso que motivo esto), lo que sobre a repuestos.
  const propServicios = Math.min(monto, deudaServicios);
  const [montoServicios, setMontoServicios] = useState(propServicios.toFixed(2));
  const [montoProductos, setMontoProductos] = useState((monto - propServicios).toFixed(2));
  const [detalle, setDetalle] = useState('');
  const [motivo, setMotivo] = useState('');
  const [clave, setClave] = useState('');
  const [procesando, setProcesando] = useState(false);
  const [error, setError] = useState('');

  const confirmar = async () => {
    if (!motivo.trim()) { setError('Ingresa el motivo'); return; }
    if (!clave.trim()) { setError('Ingresa la clave de administrador'); return; }
    if (destino === 'servicio' && !detalle.trim()) { setError('Escribe qué servicio se pagó'); return; }
    const mp = parseFloat(montoProductos) || 0;
    const ms = parseFloat(montoServicios) || 0;
    if (destino === 'credito') {
      if (Math.abs(mp + ms - monto) > 0.009) { setError(`Repuestos + servicios debe sumar S/ ${monto.toFixed(2)}`); return; }
      if (mp > deudaProductos + 0.009 || ms > deudaServicios + 0.009) { setError('El monto supera lo que el bus debe en esa categoría'); return; }
    }
    setError('');
    setProcesando(true);
    try {
      await pagosMantenimientoApi.mover(pago.id, {
        clave: clave.trim(), motivo: motivo.trim(), destino,
        ...(destino === 'credito' ? { monto_productos: mp, monto_servicios: ms } : { detalle: detalle.trim() }),
      });
      toast.success('Pago movido. La quincena del bus volvió a quedar pendiente.');
      onMovido();
    } catch (e: any) {
      setError(e.message || 'Error al mover el pago');
    } finally {
      setProcesando(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[90] p-4">
      <div className="bg-white rounded-lg w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b flex justify-between items-center">
          <h2 className="text-lg font-semibold flex items-center gap-2 text-blue-700">
            <ArrowRightLeft size={20} />
            Mover pago — Padrón {pago.bus_padron}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        <div className="p-6 space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
            Pago de mantenimiento de <b>S/ {monto.toFixed(2)}</b> ({etiquetaPeriodo(pago.periodo)}). La plata se queda en caja con su fecha original,
            pero deja de contar como mantenimiento: <b>la quincena del bus volverá a quedar pendiente</b>.
          </div>

          <div className="space-y-2">
            <label className="flex items-start gap-2 cursor-pointer">
              <input type="radio" checked={destino === 'credito'} onChange={() => setDestino('credito')} className="mt-1" />
              <span className="text-sm"><b>Aplicar a la deuda a crédito del bus</b><br />
                <span className="text-gray-500">Debe: repuestos S/ {deudaProductos.toFixed(2)} · servicios S/ {deudaServicios.toFixed(2)}</span></span>
            </label>
            <label className="flex items-start gap-2 cursor-pointer">
              <input type="radio" checked={destino === 'servicio'} onChange={() => setDestino('servicio')} className="mt-1" />
              <span className="text-sm"><b>Pago de un servicio al contado</b><br />
                <span className="text-gray-500">Pasa a Caja Servicios, sin tocar deudas</span></span>
            </label>
          </div>

          {destino === 'credito' ? (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium mb-1">A servicios (S/)</label>
                <input type="number" min="0" step="0.01" value={montoServicios} onChange={(e) => setMontoServicios(e.target.value)} className="w-full border rounded-lg px-3 py-2" />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">A repuestos (S/)</label>
                <input type="number" min="0" step="0.01" value={montoProductos} onChange={(e) => setMontoProductos(e.target.value)} className="w-full border rounded-lg px-3 py-2" />
              </div>
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium mb-1">Servicio que se pagó</label>
              <input type="text" maxLength={120} value={detalle} onChange={(e) => setDetalle(e.target.value)} placeholder="Ej: cambio de embrague" className="w-full border rounded-lg px-3 py-2" />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium mb-1">Motivo</label>
            <textarea value={motivo} onChange={(e) => setMotivo(e.target.value)} rows={2} placeholder="Ej: el dueño usó la plata del mantenimiento para pagar la reparación" className="w-full border rounded-lg px-3 py-2" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1 flex items-center gap-1"><Lock size={14} /> Clave de administrador</label>
            <input type="password" value={clave} onChange={(e) => setClave(e.target.value)} className="w-full border rounded-lg px-3 py-2" />
          </div>
          {error && <p className="text-sm text-red-600">{error}</p>}
          <button
            onClick={confirmar}
            disabled={procesando}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-lg disabled:opacity-50"
          >
            {procesando ? 'Procesando...' : 'Mover pago'}
          </button>
        </div>
      </div>
    </div>
  );
};

const ModalRegistrarPago = ({ bus, periodo, periodoActual, monto, onClose, onPagado }: {
  bus: any; periodo: string; periodoActual: string; monto: number; onClose: () => void; onPagado: () => void;
}) => {
  // Controles 2026-10-19: confirmar que se recibio el dinero; clave + motivo para una
  // quincena que no es la actual o cuando se registran muchos pagos seguidos.
  const [recibido, setRecibido] = useState(false);
  const [pideClave, setPideClave] = useState(periodo !== periodoActual);
  const [motivoClave, setMotivoClave] = useState(periodo !== periodoActual ? 'Esta quincena ya no es la actual.' : '');
  const [clave, setClave] = useState('');
  const [motivoAut, setMotivoAut] = useState('');
  const [metodoPago, setMetodoPago] = useState('efectivo');
  const [observaciones, setObservaciones] = useState('');
  const [capturasYape, setCapturasYape] = useState<string[]>([]);
  const [mostrarModalCaptura, setMostrarModalCaptura] = useState(false);
  const [procesando, setProcesando] = useState(false);
  const esYape = metodoPago === 'yape_plin';

  const agregarCapturas = (files: FileList | null) => {
    if (!files) return;
    Array.from(files).forEach(file => {
      const lector = new FileReader();
      lector.onload = () => setCapturasYape(prev => [...prev, lector.result as string]);
      lector.readAsDataURL(file);
    });
  };
  const quitarCaptura = (i: number) => setCapturasYape(prev => prev.filter((_, idx) => idx !== i));

  const confirmar = async () => {
    // La captura de Yape es opcional: no bloquea el registro del pago.
    if (!recibido) { toast.error('Marca la casilla «Recibí el dinero»'); return; }
    if (pideClave && (!clave || motivoAut.trim().length < 5)) { toast.error('Escribe la clave de administrador y el motivo'); return; }
    setProcesando(true);
    try {
      await pagosMantenimientoApi.crear(bus.padron, {
        periodo,
        metodo_pago: metodoPago,
        observaciones: observaciones.trim() || undefined,
        yape_capturas: esYape ? capturasYape : undefined,
        confirmo_recibido: true,
        clave: pideClave ? clave : undefined,
        motivo_autorizacion: pideClave ? motivoAut.trim() : undefined,
      });
      toast.success(`Pago de mantenimiento de ${bus.padron} registrado`);
      onPagado();
    } catch (error: any) {
      const msg = String(error?.message || '');
      if (msg.toLowerCase().includes('clave de administrador') || msg.includes('motivo')) {
        setPideClave(true);
        if (!msg.includes('incorrecta')) setMotivoClave(msg.replace(/^Se necesita la clave de administrador y un motivo: /, 'Motivo: '));
      }
      toast.error('No se registró el pago: ' + msg);
    } finally {
      setProcesando(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4">
      <div className="bg-white rounded-lg w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b flex justify-between items-center">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Wrench className="text-blue-600" size={20} />
            Pago de mantenimiento — Bus {bus.padron}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>

        <div className="p-6 space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex justify-between items-center">
            <span className="text-sm text-blue-700">Quincena</span>
            <span className="font-semibold text-blue-900">{etiquetaPeriodo(periodo)}</span>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex justify-between items-center">
            <span className="text-sm text-blue-700">Monto (fijo)</span>
            <span className="font-bold text-blue-900 text-lg">S/ {monto.toFixed(2)}</span>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Método de pago</label>
            <select value={metodoPago} onChange={(e) => setMetodoPago(e.target.value)} className="w-full border rounded-lg px-3 py-2">
              {METODOS.map(m => <option key={m.valor} value={m.valor}>{m.label}</option>)}
            </select>
          </div>

          {esYape && (
            <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-semibold text-purple-800">Comprobante de Yape (opcional)</h4>
                <button type="button" onClick={() => setMostrarModalCaptura(true)} className="text-xs px-3 py-1.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700">
                  + Agregar captura
                </button>
              </div>
              {capturasYape.length === 0 ? (
                <p className="text-xs text-purple-700">Sin capturas todavía. Es opcional, podés registrar el pago sin adjuntarla.</p>
              ) : (
                <div className="grid grid-cols-4 gap-2">
                  {capturasYape.map((c, i) => (
                    <div key={i} className="relative">
                      <img src={c} alt={`Captura ${i + 1}`} className="w-full h-16 object-cover rounded border" />
                      <button type="button" onClick={() => quitarCaptura(i)} className="absolute -top-1.5 -right-1.5 bg-red-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs">
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium mb-1">Observaciones (opcional)</label>
            <textarea value={observaciones} onChange={(e) => setObservaciones(e.target.value)} className="w-full border rounded-lg px-3 py-2" rows={2} />
          </div>

          {pideClave && (
            <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 space-y-2">
              <p className="text-sm text-amber-900 font-medium">Se necesita la clave de administrador.</p>
              {motivoClave && <p className="text-xs text-amber-800">{motivoClave}</p>}
              <input type="password" value={clave} onChange={(e) => setClave(e.target.value)} placeholder="Clave de administrador"
                     className="w-full border rounded-lg px-3 py-2" autoComplete="off" />
              <input type="text" value={motivoAut} onChange={(e) => setMotivoAut(e.target.value)} maxLength={300}
                     placeholder="Motivo (ej.: el dueño pagó tarde la quincena anterior)" className="w-full border rounded-lg px-3 py-2" />
            </div>
          )}

          <label className="flex items-start gap-3 bg-green-50 border-2 border-green-300 rounded-lg p-3 cursor-pointer">
            <input type="checkbox" checked={recibido} onChange={(e) => setRecibido(e.target.checked)} className="mt-0.5 w-5 h-5 accent-green-600" />
            <span className="text-sm text-green-900">
              <b>Recibí el dinero.</b> Confirmo que tengo en mano (o en Yape) los S/ {monto.toFixed(2)} del bus {bus.padron} en este momento.
              El aviso de cobro impreso <b>no</b> es un pago.
            </span>
          </label>

          <button
            onClick={confirmar}
            disabled={procesando || !recibido}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-lg disabled:opacity-50"
          >
            {procesando ? 'Procesando...' : `Registrar pago de S/ ${monto.toFixed(2)}`}
          </button>
        </div>
      </div>

      {mostrarModalCaptura && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[80]">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Smartphone className="w-5 h-5 text-purple-600" />
                Capturas de comprobante Yape
              </h3>
              <button onClick={() => setMostrarModalCaptura(false)} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
            </div>
            <label className="flex flex-col items-center justify-center border-2 border-dashed border-purple-300 rounded-lg py-8 cursor-pointer hover:bg-purple-50">
              <Smartphone className="w-8 h-8 text-purple-400 mb-2" />
              <span className="text-sm text-purple-700 font-medium">Click para elegir captura(s) de pantalla</span>
              <span className="text-xs text-gray-500 mt-1">Podés seleccionar varias a la vez</span>
              <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => { agregarCapturas(e.target.files); e.target.value = ''; }} />
            </label>
            {capturasYape.length > 0 && (
              <div className="grid grid-cols-3 gap-2 mt-4">
                {capturasYape.map((c, i) => (
                  <div key={i} className="relative">
                    <img src={c} alt={`Captura ${i + 1}`} className="w-full h-24 object-cover rounded border" />
                    <button type="button" onClick={() => quitarCaptura(i)} className="absolute -top-1.5 -right-1.5 bg-red-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs">
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <button onClick={() => setMostrarModalCaptura(false)} className="w-full mt-6 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700">
              Listo ({capturasYape.length} captura{capturasYape.length === 1 ? '' : 's'})
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PagoMantenimiento;
